
import { IconButton, Tooltip, useTheme } from '@mui/material';
import { LightMode as LightModeIcon, DarkMode as DarkModeIcon } from '@mui/icons-material';
import { useThemeContext } from '../../contexts/ThemeContext';

/**
 * Props for ThemeToggle component
 */
export interface ThemeToggleProps {
  /** Custom CSS class name */
  className?: string;
  
  /** Size of the icon button */
  size?: 'small' | 'medium' | 'large';
  
  /** Whether the button is disabled */
  disabled?: boolean;
  
  /** Custom tooltip text (optional, defaults to automatic) */
  tooltipText?: string;
}

/**
 * Theme toggle button component
 * 
 * Displays a sun icon for light mode and moon icon for dark mode.
 * Automatically updates based on current theme and provides toggle functionality.
 * 
 * Features:
 * - Material UI icons (LightMode/DarkMode)
 * - Accessible with proper ARIA labels
 * - Smooth theme transitions
 * - Loading state handling
 * - Tooltip with current mode info
 */
export function ThemeToggle({ 
  className, 
  size = 'medium', 
  disabled = false,
  tooltipText 
}: ThemeToggleProps) {
  const { mode, toggleTheme, isLoading } = useThemeContext();
  const theme = useTheme();
  
  // Determine icon and tooltip based on what we'll switch TO
  const isLightMode = mode === 'light';
  const icon = isLightMode ? <DarkModeIcon /> : <LightModeIcon />; // Show opposite of current mode
  const defaultTooltip = isLightMode 
    ? 'Switch to dark mode' 
    : 'Switch to light mode';
  const tooltip = tooltipText || defaultTooltip;
  
  // Determine button color based on current theme
  const iconColor = theme.palette.mode === 'dark' 
    ? theme.palette.text.secondary 
    : theme.palette.text.primary;
  
  const handleToggle = () => {
    if (!disabled && !isLoading) {
      toggleTheme();
    }
  };

  return (
    <Tooltip title={tooltip} placement="bottom">
      <span> {/* Span wrapper needed for disabled button tooltip */}
        <IconButton
          onClick={handleToggle}
          disabled={disabled || isLoading}
          size={size}
          className={className}
          sx={{
            color: iconColor,
            transition: theme.transitions.create(['color', 'transform'], {
              duration: theme.transitions.duration.short,
            }),
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              transform: 'scale(1.1)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
            },
          }}
          aria-label={tooltip}
          aria-pressed={isLightMode}
          data-testid="theme-toggle"
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default ThemeToggle;